// Legacy-build: bevat polyfills voor nieuwere JS-functies, zodat ook oudere tablets en browsers werken.
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from "pdfjs-dist/legacy/build/pdf.mjs";
// Worker wordt meegebouwd en vanaf de eigen server geserveerd (geen CDN).
import workerUrl from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";

GlobalWorkerOptions.workerSrc = workerUrl;

const cache = new Map<string, Promise<PDFDocumentProxy>>();

/**
 * PDF van een opgeslagen tekening, gecachet per bestand. Het hele bestand in één keer ophalen
 * (geen range-verzoeken): dan kan de service worker het bewaren voor gebruik zonder verbinding.
 */
export function loadPlanPdf(fileId: string): Promise<PDFDocumentProxy> {
  let p = cache.get(fileId);
  if (!p) {
    p = fetch(`/api/files/${fileId}/url`, { credentials: "same-origin" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then((data) => getDocument({ data: new Uint8Array(data) }).promise);
    p.catch(() => cache.delete(fileId));
    cache.set(fileId, p);
  }
  return p;
}

export interface FileInfo {
  kind: "pdf" | "image";
  pages: number;
  /** Afmetingen van blad 1 (PDF-punten) of de afbeelding (pixels). */
  width: number;
  height: number;
}

export const isPdf = (f: File) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);
export const isPlanImage = (f: File) => /^image\/(png|jpe?g|webp)$/.test(f.type) || /\.(png|jpe?g|webp)$/i.test(f.name);

/** Aantal bladen en verhouding van een gekozen bestand, voordat het wordt geüpload. */
export async function fileInfo(file: File): Promise<FileInfo> {
  if (isPdf(file)) {
    const task = getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
    const doc = await task.promise;
    try {
      const vp = (await doc.getPage(1)).getViewport({ scale: 1 });
      return { kind: "pdf", pages: doc.numPages, width: vp.width, height: vp.height };
    } finally {
      void task.destroy();
    }
  }
  const bmp = await createImageBitmap(file);
  const info: FileInfo = { kind: "image", pages: 1, width: bmp.width, height: bmp.height };
  bmp.close();
  return info;
}

/** Tekeningen: detail behouden, alleen zeer grote afbeeldingen verkleinen (prototype `compressImageKeep`). */
export async function compressPlanImage(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const max = 4000;
    if (Math.max(bmp.width, bmp.height) <= max && file.size < 8e6) return file;
    const s = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * s);
    c.height = Math.round(bmp.height * s);
    c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
    return await new Promise<Blob>((r) => c.toBlob((b) => r(b ?? file), "image/jpeg", 0.9));
  } catch {
    return file;
  }
}

/** Resolutie begrensd (±18 Mpx, max 5000 px breed), zoals in het prototype. */
export const pxScale = (w: number, h: number, dpr: number) =>
  Math.max(0.5, Math.min(dpr, 5000 / w, Math.sqrt(18e6 / (w * h))));
